import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Add from '@mui/icons-material/Add'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip from '../../component/StatusChip'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'
import { formatNumber } from '../../utils/numberDisplay'

const DEFAULT_SEARCH_SELECT = {
    searchStatus: '',
}

const SEARCH_SELECT_PARAMS = {
    searchStatus: 'searchStatus',
}

const getImageUrl = (path) => path ? `${process.env.REACT_APP_AWS_S3_URL}/${path}` : '/images/noimage.jpg'
const DONATION_LIST_IMAGE_SX = {
    width: 72,
    height: 44,
    objectFit: 'contain',
    display: 'block',
}

const DONATION_STATUS_CHIP = {
    0: { label: '비활성화', color: 'default', variant: 'outlined' },
    1: { label: '활성화', color: 'success' },
}

const DONATION_TABLE_COLUMNS = [
    { width: 70 },
    { width: 100 },
    { width: 160 },
    { width: 280 },
    { width: 120 },
    { width: 120 },
    { width: 110 },
    { width: 120 },
    { width: 120 },
]

const DONATION_TABLE_MIN_WIDTH = DONATION_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const NUMERIC_CELL_SX = {
    whiteSpace: 'nowrap',
}

const STATUS_CELL_SX = {
    textAlign: 'center',
}

export default function Donations({ role }) {
    const location = useLocation()
    const history = useHistory()
    const initialListState = createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText || '')
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery || '')
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const deleteInFlightRef = useRef(false)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/donation/getList', {
                page: curPage,
                offset,
                searchText: searchQuery,
                searchSelect
            })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '포인트 기부 상품 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '포인트 기부 상품 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, offset, searchQuery, searchSelect])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery,
            searchSelect,
            selectParams: SEARCH_SELECT_PARAMS,
        })

        if (location.search === nextSearch) {
            return
        }

        history.replace({
            pathname: location.pathname,
            search: nextSearch,
            ...(location.state ? { state: location.state } : {}),
        })
    }, [curPage, history, location.pathname, location.search, location.state, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        setSearchSelect((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
        setSearchQuery(searchText)

        if (curPage !== 1) {
            setCurPage(1)
        }
    }

    // 검색어와 필터를 실제 조회 조건으로 확정하고 첫 페이지부터 다시 본다.
    const searchButtonClick = () => {
        if (curPage === 1 && searchQuery === searchText) {
            getData(false)
            return
        }

        setSearchQuery(searchText)

        if (curPage !== 1) {
            setCurPage(1)
        }
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        const isInitialState = curPage === 1 && !searchText && !searchQuery && !searchSelect.searchStatus

        if (isInitialState) {
            getData(false)
            return
        }

        setSearchText('')
        setSearchQuery('')
        setSearchSelect({ ...DEFAULT_SEARCH_SELECT })
        setCurPage(1)
    }

    const hasActiveFilters = Boolean((searchQuery || '').trim())
        || String(searchSelect.searchStatus) !== String(DEFAULT_SEARCH_SELECT.searchStatus)

    // 상품 목록의 조회 실패와 필터 결과 없음을 같은 테이블 영역에서 구분한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="포인트 기부 상품 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={loading || actionLoading}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 포인트 기부 상품이 없습니다."
                    description="브랜드명/상품명 또는 상태 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={actionLoading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 포인트 기부 상품이 없습니다."
                description="포인트 기부 상품을 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    const handleDelete = (donationProductId) => {
        if (deleteInFlightRef.current) {
            return
        }

        deleteInFlightRef.current = true
        setActionLoading(true)

        const deleteData = async () => {
            try {
                const { data } = await API.post('/api/v1/admin/donation/delete', { donationProductId })

                if (data.code === 200) {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('success')
                    setMessageSnackbar('포인트 기부 상품을 삭제했습니다.')

                    if (curPage > 1 && params.length === 1) {
                        setCurPage(curPage - 1)
                    } else {
                        await getData(false)
                    }
                } else if (data.code === 403) {
                    history.push('/login')
                } else {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(data.message)
                }
            } catch (_error) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('포인트 기부 상품을 삭제하지 못했습니다.')
            } finally {
                deleteInFlightRef.current = false
                setActionLoading(false)
            }
        }

        deleteData()
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Title>포인트 기부 상품</Title>
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={actionLoading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="브랜드명/상품명"
                                type="search"
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={actionLoading}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={actionLoading}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight: 2 }} orientation="vertical" />
                            <TextField
                                label="상태"
                                select
                                name="searchStatus"
                                value={searchSelect.searchStatus}
                                onChange={updateSearchSelect}
                                disabled={actionLoading}
                                size="small"
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value={''}>전체</MenuItem>
                                <MenuItem value={1}>활성화</MenuItem>
                                <MenuItem value={0}>비활성화</MenuItem>
                            </TextField>
                        </ListFilterForm>

                    </ListFilterBar>

                    <ListTableToolbar
                        right={role >= 4 && (
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({
                                    pathname: '/new-donation',
                                    search: location.search,
                                    curPage,
                                    searchText: searchQuery,
                                    searchSelect
                                }))}
                            >
                                등록
                            </Button>
                        )}
                    />

                    <ListTableContainer>
                        <Table size="small" sx={{ minWidth: DONATION_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                            <colgroup>
                                {DONATION_TABLE_COLUMNS.map((column, index) => (
                                    <col key={index} style={{ width: column.width }} />
                                ))}
                            </colgroup>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>No</TableCell>
                                    <TableCell>썸네일</TableCell>
                                    <TableCell>브랜드명</TableCell>
                                    <TableCell>상품명</TableCell>
                                    <TableCell align="right" sx={NUMERIC_CELL_SX}>포인트</TableCell>
                                    <TableCell align="right" sx={NUMERIC_CELL_SX}>기부횟수</TableCell>
                                    <TableCell align="right" sx={NUMERIC_CELL_SX}>정렬순서</TableCell>
                                    <TableCell align="center" sx={STATUS_CELL_SX}>상태</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {params.length > 0 ? (
                                    params.map((row) => (
                                        <TableRow key={row.donationProductId} hover>
                                            <TableCell>{row.rownum}</TableCell>
                                            <TableCell>
                                                <img
                                                    src={getImageUrl(row.thumbnailImage)}
                                                    alt={row.title || '포인트 기부 상품 이미지'}
                                                    style={DONATION_LIST_IMAGE_SX}
                                                />
                                            </TableCell>
                                            <TableCell>{row.brandName || '-'}</TableCell>
                                            <TableCell className="listTableLimitText">{row.title || '-'}</TableCell>
                                            <TableCell align="right" sx={NUMERIC_CELL_SX}>{formatNumber(row.point)}</TableCell>
                                            <TableCell align="right" sx={NUMERIC_CELL_SX}>{formatNumber(row.exchangeCount)}</TableCell>
                                            <TableCell align="right" sx={NUMERIC_CELL_SX}>{row.sortOrder ?? '-'}</TableCell>
                                            <TableCell align="center" sx={STATUS_CELL_SX}>
                                                <StatusChip status={row.status} config={DONATION_STATUS_CHIP} fallbackLabel="상태 없음" />
                                            </TableCell>
                                            <TableCell align="right">
                                                <ListActionGroup disabled={actionLoading}>
                                                    <EditIcon
                                                        path={'donation'}
                                                        id={row.donationProductId}
                                                        search={location.search}
                                                        curPage={curPage}
                                                        searchText={searchQuery}
                                                        searchSelect={searchSelect}
                                                        disabled={role < 4}
                                                    />
                                                    {role >= 4 && <DeleteAlertDialog handleDelete={handleDelete} id={row.donationProductId} disabled={actionLoading} />}
                                                </ListActionGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
